import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { dealFlowApi } from '../lib/api';
import { toast } from 'sonner';
import {
  FolderArchive, Search, ArrowUpRight, FileText,
  CheckCircle2, Clock, Send, AlertCircle, XCircle, Loader2,
} from 'lucide-react';

const fmtCurrency = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0,
}).format(v || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const STATUS_META = {
  all:               { label: 'All',           icon: FolderArchive, cls: 'border-foreground/40 text-foreground bg-muted/40' },
  pending_signature: { label: 'Pending',       icon: Clock,         cls: 'border-amber-300 text-amber-700 bg-amber-50' },
  sent:              { label: 'Sent',          icon: Send,          cls: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
  signed:            { label: 'Signed',        icon: CheckCircle2,  cls: 'border-green-400 text-green-700 bg-green-50' },
  send_failed:       { label: 'Failed',        icon: AlertCircle,   cls: 'border-red-400 text-red-700 bg-red-50' },
  declined:          { label: 'Declined',      icon: XCircle,       cls: 'border-gray-400 text-gray-600 bg-gray-50' },
};

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const res = await dealFlowApi.documentVault(params);
      setDocs(res.data?.documents || []);
      setStatusCounts(res.data?.status_counts || {});
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const filterChips = useMemo(() => Object.entries(STATUS_META).map(([key, meta]) => ({
    key,
    ...meta,
    count: statusCounts[key] ?? 0,
  })), [statusCounts]);

  return (
    <div className="container mx-auto px-4 sm:px-6 py-6 space-y-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderArchive className="w-6 h-6 text-[#C68E5D]" />
            Documents & Contracts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every JV agreement that's been drafted, sent, or signed. Signed contracts open directly from here.
          </p>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {filterChips.map(chip => {
          const Icon = chip.icon;
          const active = statusFilter === chip.key;
          return (
            <button
              key={chip.key}
              onClick={() => setStatusFilter(chip.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                active ? chip.cls + ' ring-2 ring-offset-1 ring-[#C68E5D]/30' : 'border-border hover:bg-muted/50'
              }`}
              data-testid={`filter-${chip.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {chip.label}
              <span className={`text-[10px] ${active ? 'opacity-90' : 'opacity-60'}`}>
                ({chip.count})
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by investor, deal, or entity..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-sm"
          data-testid="documents-search"
        />
      </div>

      {/* Documents grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading contracts...
        </div>
      ) : docs.length === 0 ? (
        <Card className="rounded-sm">
          <CardContent className="py-12 text-center">
            <FileText className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">
              {search || statusFilter !== 'all'
                ? 'No contracts match your filters.'
                : 'No JV contracts yet. They\'ll appear here as soon as an investor accepts a deal.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {docs.map(doc => (
            <DocumentRow key={doc.id} doc={doc} />
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentRow({ doc }) {
  const meta = STATUS_META[doc.signing_status] || STATUS_META.pending_signature;
  const StatusIcon = meta.icon;
  const isSigned = doc.signing_status === 'signed';
  const tplBadgeCls = doc.contract_template_type === 'subdivide'
    ? 'border-blue-300 text-blue-700 bg-blue-50'
    : doc.contract_template_type === 'flip'
      ? 'border-orange-300 text-orange-700 bg-orange-50'
      : 'border-border text-muted-foreground';

  // Primary action: open the stored signed contract
  const primaryUrl = doc.signed_document_url;

  return (
    <Card className="rounded-sm border hover:border-[#C68E5D]/40 transition-colors" data-testid={`document-row-${doc.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <h3 className="text-sm font-semibold">{doc.deal_name}</h3>
              <Badge variant="outline" className={`text-[10px] inline-flex items-center gap-1 ${meta.cls}`}>
                <StatusIcon className="w-3 h-3" />
                {meta.label}
              </Badge>
              {doc.contract_template_type && (
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${tplBadgeCls}`}>
                  {doc.contract_template_type}
                </Badge>
              )}
            </div>

            <div className="text-xs text-muted-foreground space-y-0.5">
              <div>
                <span className="font-medium text-foreground/80">{doc.investor_name}</span>
                {doc.investor_entity && <span> · {doc.investor_entity}</span>}
                {doc.investor_email && <span className="ml-2">&lt;{doc.investor_email}&gt;</span>}
              </div>
              <div>
                Capital: <span className="font-mono text-foreground/80">{fmtCurrency(doc.capital_contributed)}</span>
                <span className="mx-2 opacity-50">·</span>
                Created: {fmtDate(doc.created_at)}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {primaryUrl && (
              <Button
                asChild
                variant={isSigned ? 'default' : 'outline'}
                size="sm"
                className={`text-xs h-8 rounded-sm gap-1.5 ${isSigned ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : ''}`}
                data-testid={`view-contract-${doc.id}`}
              >
                <a href={primaryUrl} target="_blank" rel="noopener noreferrer">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                  {isSigned ? 'View Signed Contract' : 'View Contract'}
                </a>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
